/**
 * Wiederholungsregel zusammenklicken.
 *
 * Bewusst eingeschraenkt auf die Faelle, die im Alltag vorkommen. Ein freies
 * RFC-5545-Eingabefeld waere maechtiger und faktisch unbedienbar.
 * Die Umrechnung in die Regel liegt in `lib/recurrenceForm.ts`.
 */
import { useMemo } from 'react';

import { Input } from '@/ui/primitives/Input';
import { describeRrule } from '@/lib/recurrence/rruleText.de';
import {
  FREQUENCIES, WEEKDAY_CODES, WEEKDAY_LABELS, clampCount, previewOccurrences, toRrule, unitFor,
  type Frequency, type RecurrenceEnd, type RecurrenceValue,
} from '@/modules/calendar/lib/recurrenceForm';
import { formatDe } from '@/lib/datetime';
import { cn } from '@/lib/utils/cn';
import type { LocalDate } from '@/domain/types';

interface RecurrenceEditorProps {
  value: RecurrenceValue;
  start: string;
  onChange: (value: RecurrenceValue) => void;
  /** Bearbeitet wird ein Termin einer bestehenden Serie. */
  teilEinerSerie?: boolean;
  /** Fuer wiederkehrende Aufgaben (F-24) - nur die Woerter aendern sich. */
  aufgabe?: boolean;
}

export function RecurrenceEditor({ value, start, onChange, teilEinerSerie = false, aufgabe = false }: RecurrenceEditorProps) {
  const rule = useMemo(() => toRrule(value, start), [value, start]);
  const preview = useMemo(() => describeRrule(rule), [rule]);
  const termine = useMemo(() => previewOccurrences(rule, start), [rule, start]);
  const set = (patch: Partial<RecurrenceValue>) => onChange({ ...value, ...patch });

  return (
    <div className="flex flex-col gap-3">
      <label className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-ink-muted">Wiederholung</span>
        <select
          value={value.frequency}
          onChange={(e) => set({ frequency: e.target.value as Frequency })}
          className="h-9 rounded-md border border-line bg-surface px-2 text-sm text-ink"
        >
          {FREQUENCIES.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
      </label>

      {value.frequency !== 'keine' && (
        <>
          <label className="flex items-center gap-2 text-sm text-ink-muted">
            Alle
            {/* Breite ueber die Huelle: Input bringt w-full mit, cn fuehrt nicht zusammen. */}
            <div className="w-16">
              <Input
                type="number"
                min={1}
                max={99}
                value={value.interval}
                onChange={(e) => set({ interval: Math.max(1, +e.target.value || 1) })}
                className="h-8"
              />
            </div>
            {unitFor(value.frequency, value.interval)}
          </label>

          {value.frequency === 'WEEKLY' && (
            <fieldset className="flex flex-col gap-1.5">
              <legend className="mb-1 text-sm font-medium text-ink-muted">
                An welchen Tagen
              </legend>
              <div className="flex gap-1">
                {WEEKDAY_CODES.map((code, i) => {
                  const on = value.byDay.includes(code);
                  return (
                    <button
                      key={code}
                      type="button"
                      aria-pressed={on}
                      aria-label={WEEKDAY_LABELS[i]}
                      onClick={() => set({
                        byDay: on
                          ? value.byDay.filter((d) => d !== code)
                          : [...value.byDay, code],
                      })}
                      className={cn(
                        'size-8 rounded-md border text-[12px] transition-colors',
                        on
                          ? 'border-accent bg-accent text-on-accent'
                          : 'border-line text-ink-muted hover:text-ink',
                      )}
                    >
                      {WEEKDAY_LABELS[i]}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          )}

          <fieldset className="flex flex-col gap-1.5">
            <legend className="mb-1 text-sm font-medium text-ink-muted">Endet</legend>
            <div className="flex flex-wrap items-center gap-1.5">
              {ENDEN.map((e) => (
                <button
                  key={e.value}
                  type="button"
                  aria-pressed={value.ende === e.value}
                  onClick={() => set({ ende: e.value })}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-sm transition-colors',
                    value.ende === e.value
                      ? 'border-accent bg-accent/10 text-ink'
                      : 'border-line text-ink-muted hover:border-line-strong hover:text-ink',
                  )}
                >
                  {e.label}
                </button>
              ))}
            </div>
            {value.ende === 'datum' && (
              <div className="w-44">
                <Input
                  type="date"
                  value={value.until}
                  onChange={(e) => set({ until: e.target.value as LocalDate | '' })}
                  aria-label="Letzter Tag der Serie"
                />
              </div>
            )}
            {value.ende === 'anzahl' && (
              <label className="flex items-center gap-2 text-sm text-ink-muted">
                Nach
                <div className="w-20">
                  <Input
                    type="number"
                    min={1}
                    max={999}
                    value={value.count}
                    onChange={(e) => set({ count: clampCount(+e.target.value) })}
                    className="h-8"
                  />
                </div>
                {aufgabe ? (value.count === 1 ? 'Mal' : 'Malen') : value.count === 1 ? 'Termin' : 'Terminen'}
              </label>
            )}
          </fieldset>

          <div className="flex flex-col gap-1 rounded-md bg-surface-raised px-3 py-2 text-sm text-ink-muted">
            <p>{preview}</p>
            {teilEinerSerie && (
              <p className="text-[12px]">
                Eine geänderte Wiederholung gilt ab diesem Termin oder für alle Termine, nie für einen einzelnen.
              </p>
            )}
            {termine.first.length > 0 && (
              <p className="text-[12px]" aria-label="Vorschau der Termine">
                {aufgabe ? 'Nächste Male' : 'Nächste Termine'}: {termine.first.map(tag).join(', ')}
                {/* Ab einem Termin mitten in der Serie waere "insgesamt" falsch gezaehlt. */}
                {!teilEinerSerie && termine.last && termine.total !== undefined && termine.total > termine.first.length
                  && ` … letzter am ${tag(termine.last)} (${termine.total} insgesamt)`}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const ENDEN: { value: RecurrenceEnd; label: string }[] = [
  { value: 'nie', label: 'Nie' },
  { value: 'datum', label: 'Am Datum' },
  { value: 'anzahl', label: 'Nach Anzahl' },
];

/** "Mi 30.09." - Wochentag hilft, A- und B-Wochen auseinanderzuhalten. */
function tag(value: string): string {
  return formatDe(value, 'EEEEEE dd.MM.');
}
